import {
  createSponsorRequest,
  CreateSponsorRequestPayload,
  CreateSponsorRequestResponse,
  deleteSponsorRequest,
  DeleteSponsorRequestParams,
  deleteSponsorshipProofs,
  DeleteSponsorshipProofsParams,
  getAllSponsorRequests,
  GetAllSponsorRequestsResponse,
  getSponsorRequest,
  GetSponsorRequestParams,
  GetSponsorRequestResponse,
  setStatusSponsorRequest,
  SetStatusSponsorRequestParams,
  SetStatusSponsorRequestPayload,
  SetStatusSponsorRequestResponse,
  updateSponsorRequest,
  UpdateSponsorRequestParams,
  UpdateSponsorRequestPayload,
  UpdateSponsorRequestResponse,
  uploadSignedOutcomeDocument,
  UploadSignedOutcomeDocumentParams,
  UploadSignedOutcomeDocumentResponse,
  uploadSponsorRequestFiles,
  UploadSponsorRequestFilesParams,
  UploadSponsorRequestFilesResponse,
} from 'app/api/sponsorRequest'
import { AxiosError } from 'axios'
import { ApiError, SponsorRequest } from 'EstraSponsor'
import {
  QueryClient,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query'

// Get All Sponsor requests

export const useGetAllSponsorRequests = (): UseQueryResult<
  GetAllSponsorRequestsResponse,
  AxiosError<ApiError>
> => {
  return useQuery('sponsorRequests', getAllSponsorRequests)
}

// Get Sponsor request

export const useGetSponsorRequest = (
  data: GetSponsorRequestParams
): UseQueryResult<GetSponsorRequestResponse, AxiosError<ApiError>> => {
  return useQuery(['sponsorRequest', data.sponsor_request_id], () =>
    getSponsorRequest(data)
  )
}

// Create sponsor request
export const useCreateSponsorRequest = (): UseMutationResult<
  CreateSponsorRequestResponse,
  AxiosError<ApiError>,
  CreateSponsorRequestPayload
> => {
  const queryClient = useQueryClient()
  return useMutation(createSponsorRequest, {
    onSuccess: () => {
      queryClient.invalidateQueries('sponsorRequests')
    },
  })
}

// Create sponsor request
export const useUpdateSponsorRequest = (): UseMutationResult<
  UpdateSponsorRequestResponse,
  AxiosError<ApiError>,
  UpdateSponsorRequestParams & UpdateSponsorRequestPayload
> => {
  const queryClient = useQueryClient()
  return useMutation(
    (payload) => {
      const { sponsor_request_id, ...data } = payload

      return updateSponsorRequest(
        {
          sponsor_request_id: sponsor_request_id,
        },
        data
      )
    },
    {
      onSuccess: (data, params) => {
        updateQueryClientSponsorRequest({
          queryClient: queryClient,
          sponsorRequestId: params.sponsor_request_id,
          updatedSponsorRequest: data,
        })
      },
    }
  )
}

// Set Status Sponsor request

export const useSetStatusSponsorRequest = (): UseMutationResult<
  SetStatusSponsorRequestResponse,
  AxiosError<ApiError>,
  SetStatusSponsorRequestParams & SetStatusSponsorRequestPayload
> => {
  const queryClient = useQueryClient()
  return useMutation(
    (payload) =>
      setStatusSponsorRequest(
        {
          sponsor_request_id: payload.sponsor_request_id,
        },
        {
          status: payload.status,
        }
      ),
    {
      onSuccess: (data, params) => {
        updateQueryClientSponsorRequest({
          queryClient: queryClient,
          sponsorRequestId: params.sponsor_request_id,
          updatedSponsorRequest: data,
        })
      },
    }
  )
}

// Upload Files Sponsor request

export const useUploadSponsorRequestFiles = (): UseMutationResult<
  UploadSponsorRequestFilesResponse,
  AxiosError<ApiError>,
  UploadSponsorRequestFilesParams & { formData: FormData }
> => {
  const queryClient = useQueryClient()
  return useMutation(
    (payload) => {
      const { sponsor_request_id, ...formData } = payload

      return uploadSponsorRequestFiles(
        {
          sponsor_request_id: payload.sponsor_request_id,
        },
        formData.formData
      )
    },
    {
      onSuccess: (data, params) => {
        updateQueryClientSponsorRequest({
          queryClient: queryClient,
          sponsorRequestId: params.sponsor_request_id,
          updatedSponsorRequest: data,
        })
      },
    }
  )
}

// Upload signed outcome document Sponsor request

export const useUploadSignedOutcomeDocument = (): UseMutationResult<
  UploadSignedOutcomeDocumentResponse,
  AxiosError<ApiError>,
  UploadSignedOutcomeDocumentParams & { formData: FormData }
> => {
  const queryClient = useQueryClient()
  return useMutation(
    (payload) => {
      const { sponsor_request_id, ...formData } = payload

      return uploadSignedOutcomeDocument(
        {
          sponsor_request_id: payload.sponsor_request_id,
        },
        formData.formData
      )
    },
    {
      onSuccess: (data, params) => {
        updateQueryClientSponsorRequest({
          queryClient: queryClient,
          sponsorRequestId: params.sponsor_request_id,
          updatedSponsorRequest: data,
        })
      },
    }
  )
}

// Delete Sponsor request

export const useDeleteSponsorRequest = (): UseMutationResult<
  void,
  AxiosError<ApiError>,
  DeleteSponsorRequestParams
> => {
  const queryClient = useQueryClient()
  return useMutation(deleteSponsorRequest, {
    onSuccess: (_data, payload) => {
      queryClient.invalidateQueries([
        'sponsorRequest',
        payload.sponsor_request_id,
      ])

      queryClient.setQueryData(
        'sponsorRequests',
        (previousSponsorRequests: SponsorRequest[] | undefined) =>
          previousSponsorRequests
            ? previousSponsorRequests.filter(
                (s) => s.id.toString() !== payload.sponsor_request_id
              )
            : []
      )
    },
  })
}

// UTILS

type UpdateQueryClientSponsorRequest = {
  queryClient: QueryClient
  sponsorRequestId: string
  updatedSponsorRequest: SponsorRequest
}

const updateQueryClientSponsorRequest = ({
  queryClient,
  sponsorRequestId,
  updatedSponsorRequest,
}: UpdateQueryClientSponsorRequest) => {
  queryClient.setQueryData(
    ['sponsorRequest', sponsorRequestId],
    updatedSponsorRequest
  )

  // Replace item in list with the new updated sponsor request
  queryClient.setQueryData(
    'sponsorRequests',
    (previousSponsorRequests: SponsorRequest[] | undefined) =>
      previousSponsorRequests
        ? previousSponsorRequests.map((s) =>
            s.id.toString() === sponsorRequestId ? updatedSponsorRequest : s
          )
        : []
  )
}

export const useDeleteSponsorshipProofs = (): UseMutationResult<
  void,
  AxiosError<ApiError>,
  DeleteSponsorshipProofsParams
> => {
  const queryClient = useQueryClient()
  return useMutation(deleteSponsorshipProofs, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries([
        'sponsorRequest',
        variables.sponsor_request_id,
      ])
    },
    onError: () => {
      // Handle error scenario
    },
  })
}
